/**
 * Shared constants for the sandboxed touchpoint preview. Kept dependency-free so the srcdoc builder,
 * the postMessage bridge, and the view can all import them without coupling to one another.
 */

/** Marks messages posted by the in-iframe measurement script; the parent bridge validates it (+ nonce). */
export const PREVIEW_MESSAGE_SOURCE = "tp-iframe-preview";

/** Attribute stamped on module wrappers so the measurement script can report their rects by id. */
export const PREVIEW_MODULE_ID_ATTRIBUTE = "data-tp-module-id";

/**
 * Security boundary for untrusted, user-authored HTML. INVARIANT: NEVER `allow-same-origin` and NEVER
 * `allow-top-navigation*` — those would let the frame reach portal cookies/localStorage/MSAL or redirect the
 * portal tab. `allow-popups allow-popups-to-escape-sandbox` is granted ONLY so a real user click on a link
 * can open a NEW tab (see the click handler in `iframe-runtime-scripts.ts`); script-driven `window.open`
 * stays neutralized by the nav-shim. Enforced by a unit test; never make env-configurable.
 */
export const PREVIEW_SANDBOX = "allow-scripts allow-popups allow-popups-to-escape-sandbox";

/** Schemes the preview click handler may open in a new tab; every other href stays blocked. */
export const PREVIEW_LINK_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"];

/** Keep in sync with the `moduleClassNames` arrays in email-design-preview.tsx / lp-design-preview.tsx. */
export const MODULE_CLASS_NAMES = [
  "button_block",
  "heading_block",
  "divider_block",
  "paragraph_block",
  "html_block",
  "icons_block",
  "image_block",
  "menu_block",
  "social_block",
  "text_block",
  "video_block",
  "fromEmail",
  "list_block",
  "bee-button",
  "bee-divider",
  "bee-heading",
  "bee-html-block",
  "bee-icons",
  "bee-image",
  "bee-menu",
  "bee-social",
  "bee-text",
  "bee-video",
  "bee-paragraph",
  "bee-spacer",
  "bee-list",
];

export const MODULE_SELECTOR = MODULE_CLASS_NAMES.map((className) => `.${className}`).join(", ");

/** `<script>` is added only when `executeScripts`; the sandbox + meta-CSP are the security boundary, not the sanitizer. */
export const BASE_ADD_TAGS = ["link", "style", "title"];

/**
 * Markers for the backend live-page runtime (`TouchpointService#addHeadScriptTags`/`#addBodyScriptTags`).
 * It targets the PUBLISHED page and only errors in the preview, so we strip it after sanitization while the
 * author's content scripts survive.
 */
export const BACKEND_RUNTIME_SCRIPT_MARKERS = [
  "const BASE_API_URL",
  "main.js?v=",
  "utag_data",
  "tiqcdn.com",
  "intlTelInput.js",
];

/**
 * Asset paths the backend writes alongside the runtime stripped above. The stylesheet link survives
 * sanitization, so the preview derives the library URL from it (`injectPhoneFieldScript`) rather than
 * bundling its own copy of intl-tel-input or carrying a second assets-host setting.
 */
export const PREVIEW_PHONE_STYLESHEET_PATH = "/css/intlTelInput.css";
export const PREVIEW_PHONE_SCRIPT_PATH = "/js/intlTelInput.js";

/**
 * intl-tel-input's country list and the wrapper it is moved into when detached (fullscreen popup on narrow
 * viewports) — class names of v21–28, then v29+, since the assets host decides the version. Its rows report
 * their unclipped boxes far below the list's scroll area, so the height measurement counts only these
 * elements' own boxes, and only while open (`buildMeasurementScript`). Counting the rows stretched the
 * iframe by thousands of px on opening the list, and content locked to the viewport (`min-height: 100vh`,
 * `height: 100%`) then held that height after a country was picked.
 */
export const PREVIEW_PHONE_DROPDOWN_SELECTOR = [
  ".iti__dropdown-content",
  ".iti--container",
  ".iti__country-selector",
  ".iti--detached-country-selector",
].join(", ");

/** Emulated mobile viewport width. */
export const MOBILE_VIEWPORT_WIDTH = 398;

/** Iframe height used before the first measurement arrives. */
export const INITIAL_IFRAME_HEIGHT = 600;

/** Floor so short touchpoints don't render as a cramped, cropped-looking sliver. */
export const MIN_PREVIEW_HEIGHT = 400;

/** A miss means the measurement script never ran (e.g. the sandbox was stripped) — surface it as an error. */
export const READY_TIMEOUT_MS = 6000;
